import React, { useEffect, useState } from "react";
import CustomForm from "../../../custom/Forms/CustomForm";
import { useParams } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import MultiSelectInput from "../../../custom/components/MultiSelect-Input/MultiSelectInput";
import DateRangePicker from "../../../custom/components/Date-Picker/DateRangePicker";
import DownloadExcel, { getRecords } from "../../../services/api.service";

const reportTypeSelectOptions = [
  { value: "all", label: "Select All" },
  { value: "inward", label: "Inward" },
  { value: "outward", label: "Outward" },
  { value: "returninward", label: "Return Inward" },
];

const HistoryReport = () => {
  const { id } = useParams();
  const [loadingData, setLoadingData] = useState(false);
  const [reportData, setReportData] = useState([]);

  const [warehouseSelect, setWarehouseSelect] = useState(null);
  const [warehouseSelectOptions, setWarehouseSelectOptions] = useState([
    { value: "all", label: "Select All" },
  ]);
  const [itemSelect, setItemSelect] = useState(null);
  const [itemSelectOptions, setItemSelectOptions] = useState([
    { value: "all", label: "Select All" },
  ]);
  const [reportTypeSelect, setReportTypeSelect] = useState(null);

  const [formErrors, setFormErrors] = useState({
    warehouseSelect: null,
    itemSelect: null,
    reportTypeSelect: null,
  });

  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 6);
  const [dateRange, setDateRange] = useState([sevenDaysAgo, today]);
  const [startDate, endDate] = dateRange || [];
  const [searchKeyword, setSearchKeyword] = useState("");

  const project_id = JSON.parse(localStorage.getItem("projectID"));

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const payload = {
          modelName: "warehouse_masters",
          pagination: {
            page: 1,
            pageSize: 10000,
          },
          whereCondition: {
            project_id: project_id,
            is_active: true,
          },
        };
        const response = await getRecords(payload, "/listWarehouse");

        if (response && Array.isArray(response.data)) {
          const warehouseOptions = response.data.map((warehouse) => ({
            value: warehouse.id,
            label: warehouse.warehouse_name,
          }));
          setWarehouseSelectOptions([
            { value: "all", label: "Select All" },
            ...warehouseOptions,
          ]);
        } else {
          console.error("Unexpected response format:", response);
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
      }
    };
    fetchWarehouses();
  }, []);

  useEffect(() => {
    const fetchItems = async () => {
      if (!warehouseSelect || warehouseSelect.length === 0) {
        setItemSelectOptions([{ value: "all", label: "Select All" }]);
        return;
      }

      try {
        const warehouse_ids = warehouseSelect.map((wh) => wh.value);

        const payload = {
          project_id: project_id,
          warehouse_ids: warehouse_ids,
        };

        const response = await getRecords(payload, "/inwardBuyItemList");

        if (response && response.code === 1 && Array.isArray(response.data)) {
          const itemOptions = response.data.map((item) => ({
            value: item.id,
            label: item.item_code,
            description: item.description || "",
          }));

          setItemSelectOptions([
            { value: "all", label: "Select All" },
            ...itemOptions,
          ]);
        } else {
          console.error("Unexpected response format:", response);
          setItemSelectOptions([{ value: "all", label: "Select All" }]);
        }
      } catch (error) {
        console.error("Error fetching items:", error);
        setItemSelectOptions([{ value: "all", label: "Select All" }]);
      }
    };

    fetchItems();
  }, [warehouseSelect]);

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!warehouseSelect || warehouseSelect.length === 0) {
      errors.warehouseSelect = "Warehouse is required";
      isValid = false;
    }

    if (!itemSelect || itemSelect.length === 0) {
      errors.itemSelect = "Item is required";
      isValid = false;
    }

    if (!reportTypeSelect || reportTypeSelect.length === 0) {
      errors.reportTypeSelect = "Report Type is required";
      isValid = false;
    }

    if (!startDate || !endDate) {
      errors.dateRange = "Date range is required";
      isValid = false;
    } else if (startDate.toDateString() === endDate.toDateString()) {
      errors.dateRange = "Please select a valid date range (not a single day)";
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };

  const handleWarehouseSelect = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setWarehouseSelect([]);
      setItemSelect([]);
      setReportTypeSelect([]);
      setDateRange([sevenDaysAgo, today]);
      setSearchKeyword("");
      setFormErrors((prev) => ({
        ...prev,
        warehouseSelect: null,
        itemSelect: null,
        reportTypeSelect: null,
        dateRange: null,
      }));
      setReportData([]);
      return;
    }

    const allOption = selectedOptions.find((opt) => opt.value === "all");
    if (allOption) {
      setWarehouseSelect(
        warehouseSelectOptions.filter((opt) => opt.value !== "all")
      );
    } else {
      setWarehouseSelect(selectedOptions);
    }
    setItemSelect([]);
    setReportTypeSelect([]);
    setDateRange([sevenDaysAgo, today]);
    setSearchKeyword("");
    setReportData([]);
    setFormErrors((prev) => ({
      ...prev,
      warehouseSelect: null,
      itemSelect: null,
      reportTypeSelect: null,
      dateRange: null,
    }));
  };

  const handleItemSelect = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setItemSelect([]);
      setReportTypeSelect([]);
      setDateRange([sevenDaysAgo, today]);
      setSearchKeyword("");
      setFormErrors((prev) => ({
        ...prev,
        warehouseSelect: null,
        itemSelect: null,
        reportTypeSelect: null,
        dateRange: null,
      }));
      setReportData([]);
      return;
    }

    const allOption = selectedOptions.find((opt) => opt.value === "all");
    if (allOption) {
      setItemSelect(itemSelectOptions.filter((opt) => opt.value !== "all"));
    } else {
      setItemSelect(selectedOptions);
    }
    setReportTypeSelect([]);
    setDateRange([sevenDaysAgo, today]);
    setSearchKeyword("");
    setReportData([]);
    setFormErrors((prev) => ({
      ...prev,
      warehouseSelect: null,
      itemSelect: null,
      reportTypeSelect: null,
      dateRange: null,
    }));
    setReportData([]);
  };

  const handleReportTypeSelect = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setReportTypeSelect([]);
      setFormErrors((prev) => ({ ...prev, reportTypeSelect: null }));
      setReportData([]);
      return;
    }

    const allOption = selectedOptions.find((opt) => opt.value === "all");
    if (allOption) {
      setReportTypeSelect(
        reportTypeSelectOptions.filter((opt) => opt.value !== "all")
      );
    } else {
      setReportTypeSelect(selectedOptions);
    }
    setFormErrors((prev) => ({ ...prev, reportTypeSelect: null }));
    setReportData([]);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setFormErrors((prev) => ({ ...prev, dateRange: null }));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchKeyword(value);
  };

  const handleExportData = () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    const columns = [
      { label: "Sr No", key: "srNo" },
      { label: "Item Code", key: "itemCode" },
      { label: "Item Description", key: "itemDesc" },
      { label: "QTY", key: "qty" },
      { label: "Created Date & Time", key: "createdDateTime" },
      { label: "Created By", key: "createdBy" },
      { label: "Transaction Type", key: "transactionType" },
      { label: "Invoice No", key: "invoiceNo" },
      { label: "Reference No", key: "referenceNo" },
      { label: "Reference Type", key: "referenceType" },
      { label: "Assigned To", key: "assignedTo" },
      { label: "Remark", key: "remark" },
    ];

    const rows = filteredData.map((item, index) => ({
      srNo: index + 1,
      itemCode: item["Item Code"] || "",
      itemDesc: item["Item Desc"] || "",
      qty: item.QTY || 0,
      createdDateTime: item["Created Date & Time"]
        ? new Date(item["Created Date & Time"])
            .toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
            .replace(",", "")
            .replace(/\s?(am|pm)$/i, (match) => match.toUpperCase())
        : "",
      createdBy: item["Created By"] || "",
      transactionType: item["Transaction Type"]?.replace(/_/g, " ") || "",
      invoiceNo: item["Invoice No"] || "",
      referenceNo: item["Reference No"] || "",
      referenceType: item["Reference Type"] || "",
      assignedTo: item["Assign To"] || item["Assigned To"] || "",
      remark: item.remark || "",
    }));

    // Add total row
    rows.push({
      itemCode: "Total",
      qty: totalQty,
    });

    DownloadExcel({
      fileName: "HistoryReport",
      columns: columns,
      data: rows,
      cellColor: false,
    });
  };

  const handleGo = async () => {
    if (!validateForm()) return;
    try {
      setLoadingData(true);
      const payload = {
        type: reportTypeSelect.map((opt) => opt.value),
        project_id: [project_id],
        warehouse_id: warehouseSelect.map((wh) => wh.value),
        buy_item_id: itemSelect.map((item) => item.value),
        date: {
          from: startDate ? startDate.toISOString().split("T")[0] : "",
          to: endDate ? endDate.toISOString().split("T")[0] : "",
        },
      };

      const response = await getRecords(payload, "/historyReport");

      if (response && response.code === 1 && Array.isArray(response.data)) {
        setReportData(response.data);
      } else {
        console.error("Unexpected response format:", response);
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setReportData([]);
    } finally {
      setLoadingData(false);
    }
  };

  const filteredData = reportData.filter(
    (item) =>
      searchKeyword === "" ||
      (item["Item Code"] &&
        item["Item Code"].toLowerCase().includes(searchKeyword.toLowerCase()))
  );

  const totalQty = filteredData.reduce((sum, item) => sum + (item.QTY || 0), 0);

  return (
    <CustomForm
      title={"History Report"}
      loadingData={loadingData}
      noBackButton={true}
      noSubmitButton={true}
    >
      <Row>
        <Col md="3" className="mt-3">
          <MultiSelectInput
            label="Warehouse"
            value={warehouseSelect}
            options={warehouseSelectOptions}
            onMultiSelect={(selected) => handleWarehouseSelect(selected)}
            placeholder="Select Warehouse"
            labelClassName="form-label"
            isClearable={true}
            asterisk={true}
            isLoading={loadingData}
            customError={formErrors.warehouseSelect}
          />
        </Col>
        <Col md="3" className="mt-3">
          <MultiSelectInput
            label="Item"
            value={itemSelect}
            options={itemSelectOptions}
            onMultiSelect={(selected) => handleItemSelect(selected)}
            placeholder="Select Item"
            labelClassName="form-label"
            isClearable={true}
            asterisk={true}
            customError={formErrors.itemSelect}
          />
        </Col>
        <Col md="3" className="mt-3">
          <MultiSelectInput
            label="Report Type"
            value={reportTypeSelect}
            options={reportTypeSelectOptions}
            onMultiSelect={(selected) => handleReportTypeSelect(selected)}
            placeholder="Select Report Type"
            labelClassName="form-label"
            isClearable={true}
            asterisk={true}
            customError={formErrors.reportTypeSelect}
          />
        </Col>
        <Col md="3" className="mt-3">
          <DateRangePicker
            label="Date Range"
            value={dateRange}
            onChange={handleDateRangeChange}
            className="w-64 p-2 text-center border rounded-lg shadow-md outline-none focus:ring-2 focus:ring-blue-500"
            isInvalid={!!formErrors.dateRange}
            customError={formErrors.dateRange}
          />
        </Col>
        <Col
          style={{ marginTop: "28px", paddingBottom: "20px" }}
          md="6"
          className="mt-3"
        >
          <Button
            onClick={handleGo}
            color="primary"
            type="button"
            className="waves-effect waves-light me-1"
            style={{ background: "#006eb9" }}
          >
            Go
          </Button>
        </Col>
      </Row>

      <Row className="justify-content-center mt-2">
        <Col
          style={{
            marginBottom: "0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div className="app-search d-flex d-lg-inline mt-0">
            <div className="position-relative">
              <input
                type="search"
                className="form-control ps-5"
                placeholder="Search by item code.."
                value={searchKeyword}
                onChange={handleInputChange}
                style={{
                  width: "13rem",
                  padding: "8px 15px",
                  borderRadius: "8px",
                  borderColor: "#D2D2D2",
                  outline: "none",
                  transition: "all 0.3s ease",
                  boxShadow: "none",
                  marginRight: "0.5rem",
                }}
              />
              <span className="ri-search-line"></span>
            </div>
          </div>
          <Button
            onClick={handleExportData}
            color="success"
            disabled={filteredData.length === 0}
          >
            Download CSV
          </Button>
        </Col>
        <div
          className="table mt-2"
          style={{
            maxHeight: "450px",
            overflowY: "auto",
            width: "100%",
            scrollBehavior: "smooth",
          }}
        >
          <table className="table table-hover">
            <thead
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#8AC1E8",
                zIndex: 1,
              }}
            >
              <tr style={{ borderBottom: "1px solid black" }}>
                <th style={{ whiteSpace: "nowrap" }}>Sr. No</th>
                <th style={{ whiteSpace: "nowrap" }}>Item Code</th>
                <th style={{ whiteSpace: "nowrap" }}>Item Description</th>
                <th style={{ whiteSpace: "nowrap" }}>QTY</th>
                <th style={{ whiteSpace: "nowrap" }}>Created Date & Time</th>
                <th style={{ whiteSpace: "nowrap" }}>Created By</th>
                <th style={{ whiteSpace: "nowrap" }}>Transaction Type</th>
                <th style={{ whiteSpace: "nowrap" }}>Invoice No</th>
                <th style={{ whiteSpace: "nowrap" }}>Reference No</th>
                <th style={{ whiteSpace: "nowrap" }}>Reference Type</th>
                <th style={{ whiteSpace: "nowrap" }}>Assigned To</th>
                <th style={{ whiteSpace: "nowrap" }}>Remark</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item["Item Code"]}</td>
                    <td
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                      }}
                    >
                      {item["Item Desc"]}
                    </td>
                    <td>{item.QTY}</td>
                    <td>
                      {item["Created Date & Time"]
                        ? new Date(item["Created Date & Time"])
                            .toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                            .replace(",", "")
                            .replace(/\s?(am|pm)$/i, (match) =>
                              match.toUpperCase()
                            )
                        : ""}
                    </td>
                    <td>{item["Created By"]}</td>
                    <td style={{ textTransform: "capitalize" }}>
                      {item["Transaction Type"]?.replace(/_/g, " ")}
                    </td>
                    <td>{item["Invoice No"]}</td>
                    <td>{item["Reference No"]}</td>
                    <td>{item["Reference Type"]}</td>
                    <td>{item["Assign To"] || item["Assigned To"]}</td>
                    <td>{item.remark}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center">
                    No records found
                  </td>
                </tr>
              )}
              {filteredData.length > 0 && (
                <tr
                  style={{
                    backgroundColor: "#D9D9D9",
                  }}
                >
                  <td></td>
                  <td style={{ fontWeight: "700" }}>Total</td>
                  <td></td>
                  <td style={{ fontWeight: "700" }}>{totalQty}</td>
                  <td colSpan="8"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Row>
    </CustomForm>
  );
};

export default HistoryReport;
